import RemovePriorityButton from "components/buttons/linkButtons/RemovePriorityButton/RemovePriorityButton";
import Segments from "components/navigation/segments/Segments";
import EditNotifySettings from "components/notifyStep/EditNotifySettings/EditNotifySettings";
import { RemovePriorityConfirmationModal } from "components/people";
import SectionSubHeader from "components/sectionHeader/sectionSubHeader/SectionSubHeader";
import { FeatureFlags } from "featureFlags";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { useHasPermissions } from "features/Application/hooks";
import {
  editPeopleContentPriority,
  removeContentPrioritiesDistributed,
} from "features/Library/PeopleAssignments/state/actions/commonActions";
import { ContentType, PeopleType } from "features/Library/PeopleAssignments/types";
import {
  getUpdatedPriorityItems,
  mapUserAssignmentsToPriorityItems,
} from "features/Library/PeopleAssignments/utils/peopleAssignmentUtils";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { bindAction, type ContentPriorityLevel } from "interfaces";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { Dimmer, Loader } from "semantic-ui-react";
import { Observable } from "utils";
import {
  ChangePeopleContentPriorityModal,
  EditPriorityLinkButton,
  GenericSectionHeader,
  RemoveLinkButton,
  RestrictedResource,
} from "../../../../components";
import { AssignmentPeopleContext, ContentTypesEnum, RolePermissions, TemplateTypes } from "../../../../enums";
import navigationUtils from "../../../../utils/navigationUtils";
import PeopleContent from "../Details/Content/PeopleContent";
import { reset } from "../state/GroupTemplateSlice";
import { setSelected } from "../state/TemplateContentSlice";
import { reset as resetCustomers, setSelectedIds } from "../state/TemplateCustomersSlice";
import { reset as resetPacks } from "../state/TemplatePacksSlice";
import { removeCustomers } from "../state/thunks/TemplateCustomersThunk";
import { removePacks } from "../state/thunks/TemplatePacksThunk";
import { fetchTemplate } from "../state/thunks/TemplateThunk";
import { type FlowItem, type SelectedContentMap } from "../types/models";
import { RemoveCustomersModal } from "./Customers/RemoveCustomersModal";
import EditConfiguration from "./EditConfiguration";
import EditTemplateCustomers from "./EditTemplateCustomers";
import EditTemplateEnrollment from "./EditTemplateEnrollment";
import EditTemplatePacks from "./EditTemplatePacks";
import TemplateOwnersContainer from "./Owners/TemplateOwnersContainer";
import { RemovePacksConfirmationModal } from "./Packs/RemovePacksConfirmationModal";
import TemplatePerformance from "./Performance/TemplatePerformance";

export type Props = PropsFromRedux & {
  preview?: boolean;
  basepath: string;
};

enum Pages {
  Performance = "performance",
  Configuration = "configuration",
  Enrollment = "enrollment",
  Packs = "packs",
  Customers = "customers",
  Content = "content",
  Owners = "owners",
  Notify = "notify",
}

export function EditGroupTemplate(props: Props) {
  const {
    isLoadingGroupInfo,
    groupInfo,
    isAccessAuthorized,
    fetchTemplate,
    resetTemplateState,
    resetCustomersState,
    resetPacksState,
    removePacksFromGroupTemplate,
    selectedFlowIds,
    editPriority,
    removeContentPriorities,
    setSelectedContentIds,
    selectedCustomerIds,
    removeCustomers,
    setSelectedCustomers,
    isRequested,
    preview,
    isLoaded,
    basepath,
  } = props;
  const flexiblePriorityDueDate = useFeatureFlag(FeatureFlags.FlexiblePriorityDueDate);
  const templatePerformanceEnabled = useFeatureFlag(FeatureFlags.TemplatePerformance);
  const location = useLocation();
  const { pathname } = location;
  const params = useParams();
  const onTriggerRemovePacksObserver = useMemo(() => new Observable(), []);
  const onTriggerChangePriorityObserver = useMemo(() => new Observable(), []);
  const onRemoveCustomerObserver = useMemo(() => new Observable<(onSubmit: () => void, ids: number[]) => void>(), []);
  const onClearPriorityObserver = useMemo(() => new Observable<(onSubmit: () => void) => void>(), []);
  const [selectedPackIds, setSelectedPackIds] = useState<number[]>([]);
  const [packsTabActionButton, setPacksTabActionButton] = useState<ReactNode>(null);
  const [customersTabActionButton, setCustomersTabActionButton] = useState<ReactNode>(null);
  const selectedContent = useRef<SelectedContentMap>(new Map());
  const hasManagePermission = useHasPermissions([RolePermissions.TemplatesManage]);

  const disabled = isRequested || !!preview || !hasManagePermission;

  const groupId = useMemo(
    () => parseInt(params.id!.toString()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    return () => {
      resetTemplateState();
      resetCustomersState();
      resetPacksState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTemplate(groupId, preview);
  }, [groupId, fetchTemplate, preview]);

  const navigate = useNavigate();

  const handleCustomerRemove = (ids: number[]) => {
    onRemoveCustomerObserver.notify(() => {
      removeCustomers(groupId, ids);
      setSelectedCustomers([]);
    }, ids);
  };

  const renderHeaderButtons = () => {
    if (disabled) {
      return [];
    }
    switch (currentStep) {
      case Pages.Packs:
        if (selectedPackIds.length) {
          return [<RemoveLinkButton key="remove-packs" onClick={() => onRemovePacksClick()} />];
        }
        return [packsTabActionButton];
      case Pages.Customers:
        if (selectedCustomerIds.length) {
          return [
            <RemoveLinkButton key="remove-customers" onClick={() => handleCustomerRemove(selectedCustomerIds)} />,
          ];
        }
        return [customersTabActionButton];
      case Pages.Content:
        if (selectedFlowIds?.length) {
          return [
            <EditPriorityLinkButton key="edit-priority" onClick={() => handleEditPriority()} />,
            renderClearPriorityButton(),
          ];
        }
        return [];
      default:
        return [];
    }
  };

  const onRemovePacksClick = (id?: number) => {
    onTriggerRemovePacksObserver.notify(
      () => removePacksFromGroupTemplate(groupId, id ? [id] : selectedPackIds),
      id ? 1 : selectedPackIds.length,
    );
  };
  const renderClearPriorityButton = () => {
    const assignments = [...selectedContent.current.values()];
    const unremovableFlowSelected = assignments.some((f) => !(f as FlowItem).canRemove);
    return (
      <RemovePriorityButton
        key="remove-priority-header-button"
        onClick={() => handleClearPriority(selectedFlowIds!)}
        isDisabled={unremovableFlowSelected}
        context="people"
        entityType={ContentType.Flow}
        peopleType={PeopleType.Group}
        showTooltip={unremovableFlowSelected}
        multiple
      />
    );
  };

  const handleEditPriority = (item?: FlowItem) => {
    const assignments = item ? [item] : [...selectedContent.current.values()];
    const priorityItems = mapUserAssignmentsToPriorityItems(assignments, ContentType.Flow, flexiblePriorityDueDate);
    onTriggerChangePriorityObserver.notify(() => {}, { [PeopleType.Group]: priorityItems });
  };

  const handleClearPriority = (ids: number[]) => {
    onClearPriorityObserver.notify(() => {
      const flowItems = { [ContentType.Flow]: ids };
      removeContentPriorities(groupId, AssignmentPeopleContext.TemplateGroup, flowItems);
      clearSelectedContent();
    });
  };

  const clearSelectedContent = () => {
    setSelectedContentIds({ context: ContentTypesEnum.Flows, selected: [] });
    selectedContent.current.clear();
  };

  const currentStep = useMemo(() => {
    return pathname.split("/").at(-1);
  }, [pathname]);

  const isInfoTab = currentStep === Pages.Configuration;
  const isPerformanceTab = currentStep === groupId.toString();

  const defaultUrl = useMemo<string>(() => {
    if (!isRequested && templatePerformanceEnabled) {
      return "../";
    }
    return isRequested ? `../${Pages.Enrollment}` : `../${Pages.Configuration}`;
  }, [isRequested, templatePerformanceEnabled]);

  return (
    <>
      <Dimmer active={isLoadingGroupInfo} inverted>
        <Loader active={isLoadingGroupInfo} />
      </Dimmer>

      <RestrictedResource isAuthorized={!!isAccessAuthorized}>
        <section className="nested-content">
          <GenericSectionHeader
            title={groupInfo.name || ""}
            titleForGA="Template Details"
            goBackAction={() => navigationUtils.goBackOrDefault(location, navigate, basepath)}
            buttons={renderHeaderButtons()}
          />
          <SectionSubHeader
            lastModifiedDateTime={isInfoTab ? groupInfo.dateModified : undefined}
            isUpdateInProgress={false}
            publishedStatus={"published"}
            showReportLastUpdated={isPerformanceTab}
          >
            <Segments to={`${basepath}/${groupId}`}>
              {!isRequested && templatePerformanceEnabled && <Segments.Segment label="Performance" />}
              {!isRequested && <Segments.Segment to={Pages.Configuration} label="Configuration" />}
              <Segments.Segment to={Pages.Enrollment} label="Enrollment" />
              <Segments.Segment to={Pages.Packs} label="Packs" />
              {!isRequested && <Segments.Segment to={Pages.Customers} label="Customers" />}
              <Segments.Segment to={Pages.Content} label="Content" />
              {isRequested && <Segments.Segment to={Pages.Owners} label="Owners" />}
              {!isRequested && <Segments.Segment to={Pages.Notify} label="Notify" />}
            </Segments>
          </SectionSubHeader>
          {isLoaded && (
            <Routes>
              {!isRequested && templatePerformanceEnabled && (
                <Route
                  path={"/"}
                  element={<TemplatePerformance templateId={groupId} templateName={groupInfo.name} />}
                />
              )}
              {isRequested || (
                <Route
                  path={Pages.Configuration}
                  element={<EditConfiguration isLoading={isLoadingGroupInfo} groupId={groupId} disabled={disabled} />}
                />
              )}
              <Route
                path={Pages.Enrollment}
                element={<EditTemplateEnrollment templateId={groupId} disabled={disabled} />}
              />
              <Route
                path={Pages.Packs}
                element={
                  <EditTemplatePacks
                    templateId={groupId}
                    selectedIds={selectedPackIds}
                    setSelectedIds={setSelectedPackIds}
                    onRemovePackClick={onRemovePacksClick}
                    setPacksTabActionButton={setPacksTabActionButton}
                    disabled={disabled}
                    preview={preview}
                  />
                }
              />
              {isRequested || (
                <Route
                  path={Pages.Customers}
                  element={
                    <EditTemplateCustomers
                      handleRemoveClick={handleCustomerRemove}
                      templateId={groupId}
                      disabled={disabled}
                      setCustomersTabActionButton={setCustomersTabActionButton}
                    />
                  }
                />
              )}
              <Route
                path={Pages.Content}
                element={
                  <PeopleContent
                    templateId={groupId}
                    contentMap={selectedContent.current}
                    contextMenuButtonHandlers={{
                      onEditPriority: handleEditPriority,
                      onClearPriority: handleClearPriority,
                    }}
                    disabled={disabled}
                  />
                }
              />
              {isRequested && <Route path={Pages.Owners} element={<TemplateOwnersContainer templateId={groupId} />} />}
              {isRequested || (
                <Route
                  path={Pages.Notify}
                  element={
                    <EditNotifySettings
                      templateType={TemplateTypes.ApproveGroupTemplate}
                      notificationId={`TemplateGroup_${groupId}`}
                      switchLabelText="Notify New Customers?"
                      switchTooltipText="Enable this option to automatically notify new customer admins when they are added to this template."
                      saveComplete={() => {
                        fetchTemplate(groupId, preview);
                      }}
                    />
                  }
                />
              )}
              <Route path="*" element={<Navigate to={defaultUrl} replace />} />
            </Routes>
          )}
          <RemovePriorityConfirmationModal peopleType={PeopleType.Group} removeObserver={onClearPriorityObserver} />
          <ChangePeopleContentPriorityModal
            onSave={(assignments: { [key in PeopleType]?: ContentPriorityLevel[] }) => {
              getUpdatedPriorityItems(assignments, (items: ContentPriorityLevel[]) => {
                editPriority(items, groupId, flexiblePriorityDueDate);
              });
              clearSelectedContent();
            }}
            onTriggerModalObserver={onTriggerChangePriorityObserver}
          />
          <RemovePacksConfirmationModal
            onTriggerRemovePacksObserver={onTriggerRemovePacksObserver}
            onConfirmed={() => setSelectedPackIds([])}
          />
          <RemoveCustomersModal removeObserver={onRemoveCustomerObserver} />
        </section>
      </RestrictedResource>
    </>
  );
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { groupTemplate } = state.people;

  return {
    groupInfo: groupTemplate.base.configuration,
    isLoadingGroupInfo: groupTemplate.base.isLoading,
    isAccessAuthorized: groupTemplate.base.isAccessAuthorized,
    selectedFlowIds: groupTemplate.content.content.selected.Flows,
    selectedCustomerIds: groupTemplate.customers.selectedIds,
    // need to handle the actual template status until own and vendor templates are separated on BE side
    isRequested: groupTemplate.base.isPreview,
    isLoaded: groupTemplate.base.groupId > 0,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    resetTemplateState: () => dispatch(reset()),
    resetCustomersState: () => dispatch(resetCustomers()),
    resetPacksState: () => dispatch(resetPacks()),
    fetchTemplate: bindAction(fetchTemplate, dispatch),
    removePacksFromGroupTemplate: bindAction(removePacks, dispatch),
    editPriority: (priorityLevels: ContentPriorityLevel[], peopleId: number, useFlexibleDueDate: boolean) =>
      dispatch(editPeopleContentPriority(priorityLevels, peopleId, PeopleType.TemplateGroup, useFlexibleDueDate)),
    removeContentPriorities: bindAction(removeContentPrioritiesDistributed, dispatch),
    setSelectedContentIds: bindAction(setSelected, dispatch),
    removeCustomers: bindAction(removeCustomers, dispatch),
    setSelectedCustomers: bindAction(setSelectedIds, dispatch),
  };
};

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditGroupTemplate);
export default ConnectedComponent;
